import React from 'react';
import { Settings, Package, Clock, ShieldCheck, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Settings: Settings,
  Package: Package,
  Clock: Clock,
  ShieldCheck: ShieldCheck
};

const ServicesSection = () => {
  return (
    <section id="services" className="py-24 bg-neutral-950 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-amber-400/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Our Services</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Complete Manufacturing Solutions
          </h2>
          <p className="mt-6 text-lg text-neutral-400 max-w-2xl mx-auto">
            From custom paper core manufacturing to bulk supply and fast delivery, 
            we provide end-to-end solutions for your industrial needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group relative bg-gradient-to-br from-neutral-900 to-neutral-950 border border-neutral-800 rounded-2xl p-8 hover:border-amber-400/30 transition-all duration-500 overflow-hidden"
              >
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-amber-400/10 rounded-2xl flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-300">
                      {IconComponent && <IconComponent className="w-8 h-8 text-amber-400" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-neutral-400 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <a
                        href="#contact"
                        className="inline-flex items-center gap-2 text-amber-400 font-medium hover:gap-3 transition-all"
                      >
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Index number */}
                <span className="absolute top-4 right-6 text-7xl font-bold text-neutral-800/50 group-hover:text-amber-400/10 transition-colors">
                  0{index + 1}
                </span>
              </div>
            );
          })}
        </div>

        {/* Industries We Serve */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-white text-center mb-10">Industries We Serve</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Printing & Packaging",
              "Film & Foil Winding",
              "Construction",
              "Label Manufacturing",
              "Textile Industry",
              "Paper Converting",
              "Food Packaging",
              "Industrial Winding"
            ].map((industry, index) => (
              <div
                key={index}
                className="p-4 bg-neutral-900/50 border border-neutral-800 rounded-xl text-center hover:border-amber-400/30 hover:bg-neutral-900 transition-all duration-300"
              >
                <span className="text-neutral-300 font-medium">{industry}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Core Sizes Available */}
        <div className="mt-16 p-8 bg-neutral-900/50 border border-neutral-800 rounded-2xl">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Core Sizes Available</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {["19mm", "25mm", "38mm", "40mm", "76mm", "152mm", "3 inch", "Custom"].map((size, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-neutral-800 text-amber-400 font-semibold rounded-lg border border-neutral-700 hover:border-amber-400/50 transition-colors"
              >
                {size}
              </span>
            ))}
          </div>
          <p className="text-neutral-400 text-center mt-4 text-sm">
            Custom diameters and wall thicknesses available on request
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
